import { z } from "zod";

export const OrderStatusSchema = z.enum([
  "SENT_TO_PRODUCTION",
  "IN_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "REVISION_REQUESTED",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "DELIVERED",
  "CANCELLED",
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderStatusActorSchema = z.enum([
  "system",
  "vendor",
  "client",
  "ops",
  "ops-admin",
  "review-ops",
]);
export type OrderStatusActor = z.infer<typeof OrderStatusActorSchema>;

export const OrderStatusColorTokenSchema = z.enum([
  "info",
  "warning",
  "primary",
  "danger",
  "success",
  "neutral",
]);
export type OrderStatusColorToken = z.infer<typeof OrderStatusColorTokenSchema>;

export const OrderStatusIconSchema = z.enum([
  "send",
  "play",
  "upload",
  "rotate-ccw",
  "shield-check",
  "check-circle",
  "x-circle",
]);
export type OrderStatusIcon = z.infer<typeof OrderStatusIconSchema>;

export interface OrderStatusDefinition {
  status: OrderStatus;
  clientLabel: string;
  vendorLabel: string;
  colorToken: OrderStatusColorToken;
  icon: OrderStatusIcon;
  sortOrder: number;
}

export const ORDER_STATUS_DEFINITIONS = {
  SENT_TO_PRODUCTION: {
    status: "SENT_TO_PRODUCTION",
    clientLabel: "Sent to production",
    vendorLabel: "Sent to production",
    colorToken: "info",
    icon: "send",
    sortOrder: 10,
  },
  IN_PRODUCTION: {
    status: "IN_PRODUCTION",
    clientLabel: "In production",
    vendorLabel: "In progress",
    colorToken: "warning",
    icon: "play",
    sortOrder: 20,
  },
  SUBMITTED_FOR_FEEDBACK: {
    status: "SUBMITTED_FOR_FEEDBACK",
    clientLabel: "Submitted for feedback",
    vendorLabel: "Submitted for feedback",
    colorToken: "primary",
    icon: "upload",
    sortOrder: 30,
  },
  REVISION_REQUESTED: {
    status: "REVISION_REQUESTED",
    clientLabel: "Sent for revision",
    vendorLabel: "Sent for revision",
    colorToken: "danger",
    icon: "rotate-ccw",
    sortOrder: 40,
  },
  SUBMITTED_FOR_FINAL_APPROVAL: {
    status: "SUBMITTED_FOR_FINAL_APPROVAL",
    clientLabel: "Submitted for final approval",
    vendorLabel: "Submitted for final approval",
    colorToken: "primary",
    icon: "shield-check",
    sortOrder: 50,
  },
  DELIVERED: {
    status: "DELIVERED",
    clientLabel: "Order delivered",
    vendorLabel: "Order delivered",
    colorToken: "success",
    icon: "check-circle",
    sortOrder: 60,
  },
  CANCELLED: {
    status: "CANCELLED",
    clientLabel: "Cancelled",
    vendorLabel: "Cancelled",
    colorToken: "neutral",
    icon: "x-circle",
    sortOrder: 70,
  },
} as const satisfies Record<OrderStatus, OrderStatusDefinition>;

export const ORDER_STATUSES = OrderStatusSchema.options;

const LEGAL_TRANSITIONS: Record<
  OrderStatusActor,
  Partial<Record<OrderStatus, readonly OrderStatus[]>>
> = {
  system: {},
  vendor: {
    SENT_TO_PRODUCTION: [
      "IN_PRODUCTION",
      "SUBMITTED_FOR_FEEDBACK",
      "SUBMITTED_FOR_FINAL_APPROVAL",
    ],
    IN_PRODUCTION: [
      "SUBMITTED_FOR_FEEDBACK",
      "SUBMITTED_FOR_FINAL_APPROVAL",
    ],
    REVISION_REQUESTED: [
      "IN_PRODUCTION",
      "SUBMITTED_FOR_FEEDBACK",
      "SUBMITTED_FOR_FINAL_APPROVAL",
    ],
    SUBMITTED_FOR_FEEDBACK: ["SUBMITTED_FOR_FEEDBACK"],
  },
  client: {
    SUBMITTED_FOR_FEEDBACK: ["REVISION_REQUESTED"],
    SUBMITTED_FOR_FINAL_APPROVAL: ["REVISION_REQUESTED", "DELIVERED"],
  },
  ops: {
    SENT_TO_PRODUCTION: ["CANCELLED"],
    IN_PRODUCTION: ["CANCELLED"],
    SUBMITTED_FOR_FEEDBACK: ["REVISION_REQUESTED", "CANCELLED"],
    REVISION_REQUESTED: ["CANCELLED"],
    SUBMITTED_FOR_FINAL_APPROVAL: [
      "REVISION_REQUESTED",
      "DELIVERED",
      "CANCELLED",
    ],
  },
  "ops-admin": {
    SENT_TO_PRODUCTION: OrderStatusSchema.options,
    IN_PRODUCTION: OrderStatusSchema.options,
    SUBMITTED_FOR_FEEDBACK: OrderStatusSchema.options,
    REVISION_REQUESTED: OrderStatusSchema.options,
    SUBMITTED_FOR_FINAL_APPROVAL: OrderStatusSchema.options,
    DELIVERED: OrderStatusSchema.options,
  },
  "review-ops": {
    SUBMITTED_FOR_FEEDBACK: ["SUBMITTED_FOR_FINAL_APPROVAL"],
    REVISION_REQUESTED: ["SUBMITTED_FOR_FINAL_APPROVAL"],
    SUBMITTED_FOR_FINAL_APPROVAL: ["SUBMITTED_FOR_FEEDBACK"],
  },
};

const SYSTEM_INITIAL_STATUSES = [
  "SENT_TO_PRODUCTION",
  "DELIVERED",
] as const satisfies readonly OrderStatus[];

export class IllegalOrderStatusTransitionError extends Error {
  readonly actor: OrderStatusActor;
  readonly from: OrderStatus | null;
  readonly to: OrderStatus;

  constructor(actor: OrderStatusActor, from: OrderStatus | null, to: OrderStatus) {
    super(
      `${actor} cannot transition order status from ${from ?? "INITIAL"} to ${to}`,
    );
    this.name = "IllegalOrderStatusTransitionError";
    this.actor = actor;
    this.from = from;
    this.to = to;
  }
}

export function getOrderStatusDefinition(
  status: OrderStatus,
): OrderStatusDefinition {
  return ORDER_STATUS_DEFINITIONS[status];
}

export function getLegalOrderStatusTransitions(
  status: OrderStatus | null,
  actor: OrderStatusActor,
): readonly OrderStatus[] {
  if (status === null) {
    return actor === "system" ? SYSTEM_INITIAL_STATUSES : [];
  }
  return LEGAL_TRANSITIONS[actor][status] ?? [];
}

export function isLegalOrderStatusTransition(
  from: OrderStatus | null,
  to: OrderStatus,
  actor: OrderStatusActor,
): boolean {
  return getLegalOrderStatusTransitions(from, actor).includes(to);
}

export function assertLegalOrderStatusTransition(
  from: OrderStatus | null,
  to: OrderStatus,
  actor: OrderStatusActor,
): void {
  if (!isLegalOrderStatusTransition(from, to, actor)) {
    throw new IllegalOrderStatusTransitionError(actor, from, to);
  }
}

/**
 * One order status for mixed script rows. The least-advanced script wins so an
 * order cannot read as complete while any deliverable is still actionable.
 */
export function rollupOrderStatus(statuses: readonly OrderStatus[]): OrderStatus {
  if (statuses.length === 0) return "SENT_TO_PRODUCTION";
  return statuses.reduce((earliest, status) =>
    ORDER_STATUS_DEFINITIONS[status].sortOrder <
    ORDER_STATUS_DEFINITIONS[earliest].sortOrder
      ? status
      : earliest,
  );
}

export const MarketplaceFulfillmentStatusSchema = z.enum([
  "SCRIPT",
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
  "CANCELLED",
]);
export type MarketplaceFulfillmentStatus = z.infer<
  typeof MarketplaceFulfillmentStatusSchema
>;

export const MARKETPLACE_FULFILLMENT_STATUS_LABELS = {
  SCRIPT: "Script",
  SENT_TO_PRODUCTION: "Sent to production",
  SUBMITTED_FOR_FEEDBACK: "Submitted for feedback",
  SENT_FOR_REVISION: "Sent for revision",
  SUBMITTED_FOR_FINAL_APPROVAL: "Submitted for final approval",
  ORDER_DELIVERED: "Order delivered",
  COMPLETED_PRE_ADGE: "Completed before Adge",
  CANCELLED: "Cancelled",
} as const satisfies Record<MarketplaceFulfillmentStatus, string>;

export function marketplaceFulfillmentStatusLabel(
  status: MarketplaceFulfillmentStatus,
): string {
  return MARKETPLACE_FULFILLMENT_STATUS_LABELS[status];
}

export const ACTIVE_PIPELINE_FULFILLMENT_STATUSES = [
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
] as const satisfies readonly MarketplaceFulfillmentStatus[];

export const HISTORY_FULFILLMENT_STATUSES = [
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
] as const satisfies readonly MarketplaceFulfillmentStatus[];

export const ARCHIVED_FULFILLMENT_STATUSES = [
  "CANCELLED",
] as const satisfies readonly MarketplaceFulfillmentStatus[];

export const TERMINAL_FULFILLMENT_STATUSES = [
  ...HISTORY_FULFILLMENT_STATUSES,
  ...ARCHIVED_FULFILLMENT_STATUSES,
] as const satisfies readonly MarketplaceFulfillmentStatus[];

const LEGACY_TO_CANONICAL: Record<MarketplaceFulfillmentStatus, OrderStatus> = {
  SCRIPT: "SENT_TO_PRODUCTION",
  SENT_TO_PRODUCTION: "SENT_TO_PRODUCTION",
  SUBMITTED_FOR_FEEDBACK: "SUBMITTED_FOR_FEEDBACK",
  SENT_FOR_REVISION: "REVISION_REQUESTED",
  SUBMITTED_FOR_FINAL_APPROVAL: "SUBMITTED_FOR_FINAL_APPROVAL",
  ORDER_DELIVERED: "DELIVERED",
  COMPLETED_PRE_ADGE: "DELIVERED",
  CANCELLED: "CANCELLED",
};

const CANONICAL_TO_LEGACY: Record<
  OrderStatus,
  MarketplaceFulfillmentStatus | null
> = {
  SENT_TO_PRODUCTION: "SENT_TO_PRODUCTION",
  IN_PRODUCTION: null,
  SUBMITTED_FOR_FEEDBACK: "SUBMITTED_FOR_FEEDBACK",
  REVISION_REQUESTED: "SENT_FOR_REVISION",
  SUBMITTED_FOR_FINAL_APPROVAL: "SUBMITTED_FOR_FINAL_APPROVAL",
  DELIVERED: "ORDER_DELIVERED",
  CANCELLED: "CANCELLED",
};

export function toOrderStatus(
  status: MarketplaceFulfillmentStatus,
): OrderStatus {
  return LEGACY_TO_CANONICAL[status];
}

export function getLegacyLegalTransitions(
  status: MarketplaceFulfillmentStatus,
  actor: OrderStatusActor,
): readonly MarketplaceFulfillmentStatus[] {
  return getLegalOrderStatusTransitions(toOrderStatus(status), actor)
    .map((candidate) => CANONICAL_TO_LEGACY[candidate])
    .filter(
      (candidate): candidate is MarketplaceFulfillmentStatus =>
        candidate !== null,
    );
}

export function assertLegacyOrderStatusTransition(
  from: MarketplaceFulfillmentStatus,
  to: MarketplaceFulfillmentStatus,
  actor: OrderStatusActor,
): void {
  assertLegalOrderStatusTransition(toOrderStatus(from), toOrderStatus(to), actor);
}

export function isActivePipelineFulfillmentStatus(
  status: MarketplaceFulfillmentStatus,
): boolean {
  return (ACTIVE_PIPELINE_FULFILLMENT_STATUSES as readonly string[]).includes(
    status,
  );
}

export function requiresProductionScheduling(
  status: MarketplaceFulfillmentStatus,
): boolean {
  return status === "SENT_TO_PRODUCTION";
}

export function usesNewOrderHandoffEmail(
  status: MarketplaceFulfillmentStatus | null | undefined,
): boolean {
  if (!status || status === "SCRIPT") return false;
  return isActivePipelineFulfillmentStatus(status);
}

export function rollupOrderFulfillmentStatus(
  statuses: readonly MarketplaceFulfillmentStatus[],
): MarketplaceFulfillmentStatus {
  if (statuses.length === 0) return "SENT_TO_PRODUCTION";
  return statuses.reduce((earliest, status) =>
    LEGACY_FULFILLMENT_STATUS_RANK[status] <
    LEGACY_FULFILLMENT_STATUS_RANK[earliest]
      ? status
      : earliest,
  );
}

const LEGACY_FULFILLMENT_STATUS_RANK: Record<
  MarketplaceFulfillmentStatus,
  number
> = {
  SCRIPT: 0,
  SENT_TO_PRODUCTION: 1,
  SUBMITTED_FOR_FEEDBACK: 2,
  SENT_FOR_REVISION: 3,
  SUBMITTED_FOR_FINAL_APPROVAL: 4,
  ORDER_DELIVERED: 5,
  COMPLETED_PRE_ADGE: 6,
  CANCELLED: 7,
};
